import { InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';

import { useTrackPageViewAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Footer } from '../../../domains/Layout/Footer/Footer';
import { Head } from '../../../domains/Layout/Head/Head';
import { useJsonLd } from '../../../domains/Layout/Head/JsonLd';
import { imageLoaderForId } from '../../../domains/Layout/Image/URL/ImageURL';
import { PrimaryNav } from '../../../domains/Layout/PrimaryNav/PrimaryNav';
import { Cover } from '../../../partials/marketing-destination-details/Cover/Cover';
import { getServerSideProps } from '../../../partials/marketing-destination-details/getServerSideProps';
import { Slice } from '../../../partials/marketing-destination-details/Slice';
import { useLocale } from '../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { useCurrentSite } from '../../../shared/providers/currentSite/useCurrentSite';

export { getServerSideProps };

export type PageMarketingDestinationDetailsProps = InferGetServerSidePropsType<
    typeof getServerSideProps
>;

export default function PageMarketingDestinationDetails(
    props: PageMarketingDestinationDetailsProps,
) {
    const router = useRouter();
    const { locale } = useLocale();
    const { currentSite } = useCurrentSite();
    const { generateUrl } = useUrlGenerator();

    const { primaryNav, hrefLangs, previewMode, marketingDestinationDetailsDocument } = props.data;

    useTrackPageViewAmplitude((ampli, defaultProperties) => {
        ampli.viewedPageDestinationDetails({
            ...defaultProperties,
        });
    });
    const {
        body,
        cover,
        coverCopyright,
        link,
        metadata,
        slug,
        supportingTitle,
        title,
        modalCtaLabel,
        newsletterModal,
        withNewsletterModal,
    } = marketingDestinationDetailsDocument;

    const metaTitle = metadata.title || undefined;
    const metaDescription = metadata.description || undefined;
    const canonical = generateUrl('marketing_destination_details', {
        slug: slug,
    });

    const ogTitle = metadata.og.title || metaTitle || undefined;
    const ogDescription = metadata.og.description || metaDescription || undefined;
    const ogImage =
        metadata.og.image?.url ?? marketingDestinationDetailsDocument.cover?.url ?? undefined;

    return (
        <>
            <Head
                title={metaTitle}
                description={metaDescription}
                canonical={canonical}
                hrefLangs={hrefLangs}
                robots="noindex"
                og={{
                    title: ogTitle,
                    description: ogDescription,
                    image: ogImage
                        ? imageLoaderForId({
                              aspectRatio: 1.91,
                          })({
                              src: ogImage,
                              width: 1200,
                          })
                        : cover?.url
                          ? imageLoaderForId({
                                aspectRatio: 1.91,
                            })({
                                src: cover.url,
                                width: 1200,
                            })
                          : undefined,
                    locale,
                    url: currentSite.url + router.asPath,
                    type: 'website',
                    site_name: currentSite.organisation.name,
                }}
                jsonLd={useJsonLd([])}
            />

            <PrimaryNav contextContinent={null} previewMode={previewMode} {...primaryNav} />

            <div className="mb-40 last:mb-0">
                <Cover
                    title={title ?? ''}
                    supportingTitle={supportingTitle ?? ''}
                    cover={cover ?? { url: '', alt: '', width: 0, height: 0 }}
                    coverCopyright={coverCopyright}
                    link={link ?? { url: '', label: '' }}
                    modalCtaLabel={modalCtaLabel}
                    newsletterModal={withNewsletterModal ? newsletterModal : null}
                />
            </div>

            <div className="layout-with-centered-content mb-72 md:mb-112">
                {body.map((slice, index) => {
                    if (slice === null) {
                        return null;
                    }

                    return (
                        <section
                            key={index}
                            className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96"
                        >
                            <Slice slice={slice} row={index + 1} />
                        </section>
                    );
                })}
            </div>

            <Footer
                noBrand
                topDestinations={primaryNav.destinationsMenu?.world.topDestinations ?? []}
            />
        </>
    );
}
